// ============================================
// Admin Referrals Page
// View all submitted referrals
// ============================================

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { clearDashboardSessions, getActiveDashboardSession, getDashboardToken } from '../utils/session';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://54.144.102.147:3001/api';

// ── Constants matching the referral form ──────────────────────────────
const HOUSING_OPTIONS = [
  'Staying in a shelter',
  'Living in a motel or hotel temporarily',
  'No fixed or consistent nighttime residence',
  'Staying somewhere not intended for regular housing',
  'Leaving an institution with no stable housing',
  'At risk of losing housing within 30 days',
  'Escaping domestic violence, abuse, stalking, or other unsafe conditions',
  'None of the above',
];

const CONDITION_OPTIONS = [
  'Diabetes',
  'High Blood Pressure (Hypertension)',
  'Anxiety',
  'Depression',
  'Heart Disease',
  'Coronary Artery Disease',
  'Kidney Disease',
  'Liver Disease',
  'Asthma',
  'Chronic Obstructive Pulmonary Disease (COPD)',
  'Bipolar Disorder',
  'Psychotic Disorders',
  'Substance Use Disorder',
  'Other medical or behavioral health conditions',
];

interface Referral {
  id: number;
  referralId: string;
  fullName: string;
  dateOfBirth: string;
  phone?: string;
  email?: string;
  county: string;
  preferredLanguage?: string;
  preferredLanguageOther?: string;
  benefitsIdNumber?: string;
  housingInsecurity?: string;
  housingInsecurityOther?: string;
  erVisits?: string;
  diagnosedConditions?: string;
  diagnosedConditionsOther?: string;
  additionalInfo?: string;
  createdAt: string;
}

export const AdminReferralsPage: React.FC = () => {
  const navigate = useNavigate();
  const dashboardSession = getActiveDashboardSession();
  const isAdmin = dashboardSession?.role === 'admin';
  const dashboardTitle = isAdmin ? 'Admin Dashboard' : 'Staff Dashboard';
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    const token = getDashboardToken();
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchReferrals();
  }, [page, navigate]);

  const fetchReferrals = async () => {
    setLoading(true);
    setError('');
    try {
      const token = getDashboardToken();
      const params: any = { page, limit: 20 };
      if (search) params.search = search;

      const response = await axios.get(`${API_BASE_URL}/referrals`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      setReferrals(response.data.referrals);
      setPagination(response.data.pagination);
    } catch (err: any) {
      if (err.response?.status === 401) {
        clearDashboardSessions();
        navigate('/admin/login');
      } else {
        setError('Failed to load referrals');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchReferrals();
  };

  const handleLogout = () => {
    const nextPath = dashboardSession?.role === 'staff' ? '/staff/login' : '/admin/login';
    clearDashboardSessions();
    navigate(nextPath);
  };

  const formatDate = (dateString: string) => new Date(dateString).toLocaleString();

  // Parse comma-separated housing string into array
  const housingCount = (val?: string) => {
    if (!val) return 0;
    return val.split(',').map((s) => s.trim()).filter(Boolean).length;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <img src="/css_logo.png" alt="CSS" className="h-10" />
              <h1 className="text-lg font-bold text-gray-900 hidden sm:block">{dashboardTitle}</h1>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/admin/submissions" className="text-sm text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md hover:bg-gray-100">
                Submissions
              </Link>
              {isAdmin && (
                <Link to="/admin/users" className="text-sm text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md hover:bg-gray-100">
                  Users
                </Link>
              )}
              <Link to="/admin/referrals" className="text-sm text-blue-700 font-medium px-3 py-2 rounded-md bg-blue-50">
                Referrals
              </Link>
              <Link to="/submit-forms" className="text-sm text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md hover:bg-gray-100">
                Main Form
              </Link>
              <button onClick={handleLogout} className="text-sm text-red-600 hover:text-red-700 px-3 py-2">
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats + Search */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Referrals</p>
              <p className="text-2xl font-bold text-gray-900">{pagination.total}</p>
            </div>
            <form onSubmit={handleSearch} className="flex gap-2">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, county, email..."
                className="border border-gray-300 rounded-lg px-4 py-2 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700">
                Search
              </button>
            </form>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading referrals...</div>
        ) : referrals.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No referrals found</div>
        ) : (
          <>
            {/* Table */}
            <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Full Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">DOB</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">County</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Housing</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {referrals.map((ref, idx) => (
                    <React.Fragment key={ref.referralId}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {(page - 1) * pagination.limit + idx + 1}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{ref.fullName}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{ref.dateOfBirth}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{ref.phone || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{ref.county}</td>
                        <td className="px-4 py-3 text-sm">
                          {housingCount(ref.housingInsecurity) > 0 ? (
                            <span className="bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded text-xs font-medium">
                              {housingCount(ref.housingInsecurity)} selected
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">{formatDate(ref.createdAt)}</td>
                        <td className="px-4 py-3 text-sm">
                          <button
                            onClick={() => setExpandedId(expandedId === ref.referralId ? null : ref.referralId)}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            {expandedId === ref.referralId ? 'Hide' : 'View'}
                          </button>
                        </td>
                      </tr>
                      {expandedId === ref.referralId && (
                        <tr>
                          <td colSpan={8} className="p-0">
                            <ReferralFormView referral={ref} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex justify-center items-center gap-4 mt-6">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 text-sm border rounded-lg disabled:opacity-50 hover:bg-gray-100"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">Page {page} of {pagination.totalPages}</span>
                <button
                  onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={page === pagination.totalPages}
                  className="px-4 py-2 text-sm border rounded-lg disabled:opacity-50 hover:bg-gray-100"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ============================================
// Read-only referral form view — matches the new form layout exactly
// ============================================

const ReferralFormView: React.FC<{ referral: Referral }> = ({ referral: r }) => {
  // Parse comma-separated strings back to arrays
  const housingSelected = r.housingInsecurity
    ? r.housingInsecurity.split(',').map((s) => s.trim()).filter(Boolean)
    : [];
  const conditionsSelected = r.diagnosedConditions
    ? r.diagnosedConditions.split(',').map((s) => s.trim()).filter(Boolean)
    : [];

  return (
    <div className="bg-blue-50 p-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-1">
          <img src="/css_logo.png" alt="CSS" className="h-10" />
          <h2 className="text-lg font-bold text-gray-900">California Support Services</h2>
        </div>
        <p className="text-gray-500 text-xs mb-2 ml-1">
          Enhanced Care Management (ECM) Interest &amp; Referral Form
        </p>
        <p className="text-gray-400 text-xs mb-4 ml-1">
          Submitted on {new Date(r.createdAt).toLocaleString()}
        </p>

        {/* Form card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-5 space-y-6">

            {/* Participant Information */}
            <SectionLabel>Participant Information</SectionLabel>

            {/* 1 */}
            <ROQuestion number={1} title="Participant's Full Name" required>
              <ROAnswer value={r.fullName} />
            </ROQuestion>

            {/* 2 */}
            <ROQuestion number={2} title="Date of Birth">
              <ROAnswer value={r.dateOfBirth} />
            </ROQuestion>

            {/* 3 */}
            <ROQuestion number={3} title="Contact Phone Number">
              <ROAnswer value={r.phone} />
            </ROQuestion>

            {/* 4 */}
            <ROQuestion number={4} title="Email Address">
              <ROAnswer value={r.email} />
            </ROQuestion>

            {/* 5 */}
            <ROQuestion number={5} title="County of Residence" required>
              <ROAnswer value={r.county} />
            </ROQuestion>

            {/* 6 */}
            <ROQuestion number={6} title="Preferred Language">
              <RORadio
                options={['English', 'Spanish']}
                selected={r.preferredLanguage}
                otherValue={r.preferredLanguageOther}
                showOther
              />
            </ROQuestion>

            {/* 7 */}
            <ROQuestion number={7} title="Medi‑Cal Benefits Identification Card (BIC) Number">
              <div className="mb-3">
                <img src="/placeholder2.png" alt="Benefits Identification Card" className="rounded-lg max-w-full h-auto" />
              </div>
              <ROAnswer value={r.benefitsIdNumber} />
            </ROQuestion>

            {/* Housing Situation */}
            <SectionLabel>Housing Situation</SectionLabel>

            {/* 8 */}
            <ROQuestion number={8} title="Are you currently experiencing any housing instability? (Select all that apply)">
              <ROCheckboxes
                options={HOUSING_OPTIONS}
                selected={housingSelected}
                otherLabel="Other (please describe)"
                otherValue={r.housingInsecurityOther}
              />
            </ROQuestion>

            {/* Healthcare Utilization */}
            <SectionLabel>Healthcare Utilization</SectionLabel>

            {/* 9 */}
            <ROQuestion number={9} title="Within the past six months, have you visited an emergency room or urgent care center three (3) or more times?">
              <RORadio options={['Yes', 'No']} selected={r.erVisits} />
            </ROQuestion>

            {/* Health Conditions */}
            <SectionLabel>Health Conditions</SectionLabel>

            {/* 10 */}
            <ROQuestion number={10} title="Have you ever been diagnosed with any of the following? (Select all that apply)">
              <ROCheckboxes
                options={CONDITION_OPTIONS}
                selected={conditionsSelected}
                otherLabel="Other medical or behavioral health conditions"
                otherValue={r.diagnosedConditionsOther}
              />
            </ROQuestion>

            {/* Additional Notes */}
            <SectionLabel>Additional Notes</SectionLabel>

            {/* 11 */}
            <ROQuestion number={11} title="Additional details you would like us to know (optional).">
              <ROAnswer value={r.additionalInfo} multiline />
            </ROQuestion>

          </div>
        </div>
      </div>
    </div>
  );
};

// ── Read-only sub-components ──────────────────────────────────────────

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-xs font-bold text-blue-800 uppercase tracking-wide border-t border-gray-100 pt-4">{children}</p>
);

const ROQuestion: React.FC<{
  number: number;
  title: string;
  required?: boolean;
  children: React.ReactNode;
}> = ({ number, title, required, children }) => (
  <div>
    <div className="flex items-start gap-3 mb-2">
      <span className="flex-shrink-0 w-6 h-6 bg-blue-800 text-white text-xs font-bold rounded flex items-center justify-center mt-0.5">
        {number}
      </span>
      <h3 className="text-sm font-medium text-gray-900">
        {title}
        {required && <span className="text-red-600 ml-1">*</span>}
      </h3>
    </div>
    <div className="pl-9">{children}</div>
  </div>
);

const ROAnswer: React.FC<{ value?: string; multiline?: boolean }> = ({ value, multiline }) => (
  <div className="border-b border-gray-200 py-1.5 min-h-[28px]">
    {multiline ? (
      <p className="text-sm text-gray-900 whitespace-pre-wrap">
        {value || <span className="text-gray-400 italic">No answer</span>}
      </p>
    ) : (
      <p className="text-sm text-gray-900">
        {value || <span className="text-gray-400 italic">No answer</span>}
      </p>
    )}
  </div>
);

const RORadio: React.FC<{
  options: string[];
  selected?: string;
  showOther?: boolean;
  otherValue?: string;
}> = ({ options, selected, showOther, otherValue }) => (
  <div className="space-y-1.5">
    {options.map((option) => (
      <label key={option} className="flex items-center gap-2">
        <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${selected === option ? 'border-blue-600' : 'border-gray-300'}`}>
          {selected === option && <span className="w-2 h-2 rounded-full bg-blue-600" />}
        </span>
        <span className="text-sm text-gray-700">{option}</span>
      </label>
    ))}
    {showOther && (
      <label className="flex items-center gap-2">
        <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${selected === '__other__' ? 'border-blue-600' : 'border-gray-300'}`}>
          {selected === '__other__' && <span className="w-2 h-2 rounded-full bg-blue-600" />}
        </span>
        <span className="text-sm text-gray-700">
          {selected === '__other__' && otherValue ? otherValue : 'Other'}
        </span>
      </label>
    )}
  </div>
);

const ROCheckboxes: React.FC<{
  options: string[];
  selected: string[];
  otherLabel?: string;
  otherValue?: string;
}> = ({ options, selected, otherLabel, otherValue }) => {
  const isOtherSelected = selected.includes('__other__');

  return (
    <div className="space-y-1.5">
      {options.map((option) => (
        <label key={option} className="flex items-start gap-2">
          <span className={`w-4 h-4 mt-0.5 rounded border-2 flex items-center justify-center flex-shrink-0 ${selected.includes(option) ? 'border-blue-600 bg-blue-600' : 'border-gray-300'}`}>
            {selected.includes(option) && (
              <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </span>
          <span className="text-sm text-gray-700">{option}</span>
        </label>
      ))}
      {otherLabel && (
        <label className="flex items-start gap-2">
          <span className={`w-4 h-4 mt-0.5 rounded border-2 flex items-center justify-center flex-shrink-0 ${isOtherSelected ? 'border-blue-600 bg-blue-600' : 'border-gray-300'}`}>
            {isOtherSelected && (
              <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </span>
          <span className="text-sm text-gray-700">
            {otherLabel}{isOtherSelected && otherValue ? `: ${otherValue}` : ''}
          </span>
        </label>
      )}
    </div>
  );
};

export default AdminReferralsPage;
